import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth-store'
import { getProfile, listOrganizations } from '@/lib/org-api-client'
import type { Profile, Organization } from '@knownow/domain'

export interface UseProfileReturn {
  readonly profile: Profile | null
  readonly organizations: readonly Organization[]
  readonly loading: boolean
  readonly defaultOrgSlug: string | null
}

export function useProfile(): UseProfileReturn {
  const user = useAuthStore((s) => s.user)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [organizations, setOrganizations] = useState<readonly Organization[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    async function fetchData(): Promise<void> {
      try {
        const [profileData, orgsData] = await Promise.all([
          getProfile(),
          listOrganizations(),
        ])
        setProfile(profileData)
        setOrganizations(orgsData)
      } catch (error: unknown) {
        console.error('Failed to fetch profile data:', error)
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [user])

  const defaultOrg = organizations.find(
    (o) => o.id === profile?.defaultOrganizationId,
  ) ?? organizations[0] ?? null

  return {
    profile,
    organizations,
    loading,
    defaultOrgSlug: defaultOrg?.slug ?? null,
  }
}
